import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';

/**
 * QuartierAutocomplete
 *
 * Champ "Quartier" pour le formulaire d'annonce/admin Fynho.
 * - Tape le nom -> propose les quartiers deja en base (avec coordonnees)
 * - Si le quartier n'existe pas -> ouvre une mini carte pour placer un pin,
 *   enregistre le nouveau quartier, puis le selectionne automatiquement.
 *
 * Props:
 *  - apiBaseUrl: ex "https://fynho.com/api"
 *  - ville: filtre optionnel (ex "Douala")
 *  - onSelect(quartier): callback avec { id, nom, ville, latitude, longitude, region }
 *  - initialValue: nom de quartier pre-rempli (edition d'annonce existante)
 */
export default function QuartierAutocomplete({ apiBaseUrl, ville, onSelect, initialValue }) {
  const [query, setQuery] = useState(initialValue || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [pickedCoords, setPickedCoords] = useState(null);
  const [savingNouveau, setSavingNouveau] = useState(false);
  const debounceRef = useRef(null);

  const centreCarteParDefaut = ville === 'Yaoundé' || ville === 'Yaounde'
    ? [3.8480, 11.5021]
    : [4.0511, 9.7679]; // Douala par defaut

  const rechercherQuartiers = useCallback((texte) => {
    if (!texte || texte.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    const params = new URLSearchParams({ q: texte });
    if (ville) params.append('ville', ville);

    fetch(apiBaseUrl + '/quartiers-search.php?' + params.toString())
      .then((res) => res.json())
      .then((data) => {
        // Le PHP renvoie {"success":true,"data":{"results":[...]}} - on lit data.data
        const payload = data && data.data ? data.data : data;
        setSuggestions((payload && payload.results) || []);
        setLoading(false);
      })
      .catch(() => {
        setSuggestions([]);
        setLoading(false);
      });
  }, [apiBaseUrl, ville]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      rechercherQuartiers(query);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, rechercherQuartiers]);

  function handleInputChange(e) {
    setQuery(e.target.value);
    setSelected(null);
    setShowDropdown(true);
  }

  function handleSelectQuartier(quartier) {
    setSelected(quartier);
    setQuery(quartier.nom);
    setShowDropdown(false);
    setSuggestions([]);
    if (onSelect) onSelect(quartier);
  }

  function ouvrirCartePourNouveauQuartier() {
    setShowDropdown(false);
    setShowMapPicker(true);
    setPickedCoords(null);
  }

  function MapClickHandler() {
    useMapEvents({
      click(e) {
        setPickedCoords({ lat: e.latlng.lat, lng: e.latlng.lng });
      },
    });
    return null;
  }

  function confirmerNouveauQuartier() {
    if (!pickedCoords || !query.trim()) return;
    setSavingNouveau(true);

    fetch(apiBaseUrl + '/quartiers-add.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        nom: query.trim(),
        ville: ville || 'Douala',
        latitude: pickedCoords.lat,
        longitude: pickedCoords.lng,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setSavingNouveau(false);
        const payload = data && data.data ? data.data : data;
        if (data.success && payload && payload.quartier) {
          handleSelectQuartier(payload.quartier);
          setShowMapPicker(false);
        } else {
          alert(data.message || data.error || "Erreur lors de l'enregistrement du quartier");
        }
      })
      .catch(() => {
        setSavingNouveau(false);
        alert('Erreur reseau lors de l\'enregistrement du quartier');
      });
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#e5c76b' }}>
        Quartier
      </label>
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={() => setShowDropdown(true)}
        placeholder="Ex: Bonapriso, Bastos..."
        style={{
          width: '100%',
          padding: '10px 12px',
          borderRadius: 8,
          border: selected ? '1px solid #4caf50' : '1px solid #444',
          background: '#1a1a1a',
          color: '#fff',
        }}
      />

      {selected && (
        <div style={{ fontSize: 12, color: '#4caf50', marginTop: 4 }}>
          ✓ Coordonnees confirmees ({selected.latitude.toFixed(4)}, {selected.longitude.toFixed(4)})
        </div>
      )}

      {showDropdown && query.trim().length >= 2 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#222',
            border: '1px solid #444',
            borderRadius: 8,
            marginTop: 4,
            maxHeight: 240,
            overflowY: 'auto',
            zIndex: 50,
          }}
        >
          {loading && (
            <div style={{ padding: 10, color: '#999', fontSize: 13 }}>Recherche...</div>
          )}

          {!loading && suggestions.map((quartier) => (
            <div
              key={quartier.id}
              onClick={() => handleSelectQuartier(quartier)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #333',
                color: '#fff',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#333')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ fontWeight: 600 }}>{quartier.nom}</div>
              <div style={{ fontSize: 12, color: '#999' }}>{quartier.ville}</div>
            </div>
          ))}

          {!loading && suggestions.length === 0 && (
            <div
              onClick={ouvrirCartePourNouveauQuartier}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                color: '#e5c76b',
              }}
            >
              ＋ "{query}" introuvable — le placer sur la carte
            </div>
          )}
        </div>
      )}

      {showMapPicker && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.75)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ background: '#1a1a1a', borderRadius: 12, padding: 16, width: '90%', maxWidth: 500 }}>
            <h3 style={{ color: '#e5c76b', marginBottom: 8 }}>
              Placer "{query}" sur la carte
            </h3>
            <p style={{ color: '#aaa', fontSize: 13, marginBottom: 10 }}>
              Clique sur l'emplacement exact du quartier. Cette position sera reutilisee pour toutes les futures annonces de ce quartier.
            </p>
            <div style={{ height: 300, borderRadius: 8, overflow: 'hidden' }}>
              <MapContainer center={centreCarteParDefaut} zoom={13} style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <MapClickHandler />
                {pickedCoords && <Marker position={[pickedCoords.lat, pickedCoords.lng]} />}
              </MapContainer>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button
                onClick={() => setShowMapPicker(false)}
                style={{ flex: 1, padding: 10, borderRadius: 8, background: '#333', color: '#fff', border: 'none' }}
              >
                Annuler
              </button>
              <button
                onClick={confirmerNouveauQuartier}
                disabled={!pickedCoords || savingNouveau}
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 8,
                  background: pickedCoords ? '#e5c76b' : '#555',
                  color: '#000',
                  border: 'none',
                  fontWeight: 600,
                }}
              >
                {savingNouveau ? 'Enregistrement...' : 'Confirmer le pin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}